import React, { useEffect, useMemo, useState } from "react";

const CONSTRUCTORS_STORAGE_KEY = "yoma_constructors";

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER = "#D8C9B8";
const VERT = "#5C8A5C";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

const SPECIALITES = [
  "Maçonnerie",
  "Gros œuvre",
  "Second œuvre",
  "Plomberie",
  "Électricité",
  "Menuiserie",
  "VRD",
  "Tous corps d’état",
];

const STATUTS = ["Actif", "En test", "Bloqué"];

const defaultForm = () => ({
  entreprise: "",
  responsable: "",
  telephone: "",
  email: "",
  specialite: "Maçonnerie",
  zone: "",
  statut: "Actif",
  commentaire: "",
});

function initiale(nom) {
  if (!nom || typeof nom !== "string") return "?";
  const t = nom.trim();
  return t ? t[0].toUpperCase() : "?";
}

export default function ConstructorsPage({ cabinetId = null }) {
  const [constructors, setConstructors] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const [form, setForm] = useState(defaultForm);

  const [editingId, setEditingId] = useState(null);

  const [filterSpecialite, setFilterSpecialite] = useState("toutes");
  const [filterStatut, setFilterStatut] = useState("tous");
  const [search, setSearch] = useState("");

  const [companyName, setCompanyName] = useState("MON CABINET");

  useEffect(() => {
    try {
      const n = localStorage.getItem("yoma_company_name");
      if (n) setCompanyName(n);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONSTRUCTORS_STORAGE_KEY);
      if (saved) {
        setConstructors(JSON.parse(saved));
      }
    } catch (error) {
      console.error("Erreur chargement constructeurs:", error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    try {
      localStorage.setItem(CONSTRUCTORS_STORAGE_KEY, JSON.stringify(constructors));
    } catch (error) {
      console.error("Erreur sauvegarde constructeurs:", error);
    }
  }, [constructors, isLoaded]);

  function updateField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function ajouterConstructeur() {
    if (!form.entreprise || !form.responsable) return;

    const nouveauConstructeur = {
      id: Date.now(),
      ...form,
      dateCreation: new Date().toLocaleDateString("fr-FR"),
    };

    setConstructors((prev) => [...prev, nouveauConstructeur]);

    setForm(defaultForm());
    setEditingId(null);
  }

  function enregistrerModification() {
    if (!editingId || !form.entreprise || !form.responsable) return;

    setConstructors((prev) =>
      prev.map((item) =>
        item.id === editingId
          ? {
              ...item,
              entreprise: form.entreprise,
              responsable: form.responsable,
              telephone: form.telephone,
              email: form.email,
              specialite: form.specialite,
              zone: form.zone,
              statut: form.statut,
              commentaire: form.commentaire,
            }
          : item
      )
    );

    setForm(defaultForm());
    setEditingId(null);
  }

  function commencerEdition(item) {
    setEditingId(item.id);
    setForm({
      entreprise: item.entreprise || "",
      responsable: item.responsable || "",
      telephone: item.telephone || "",
      email: item.email || "",
      specialite: item.specialite || "Maçonnerie",
      zone: item.zone || "",
      statut: item.statut || "Actif",
      commentaire: item.commentaire || "",
    });
  }

  function annulerEdition() {
    setEditingId(null);
    setForm(defaultForm());
  }

  function supprimerConstructeur(id) {
    setConstructors((prev) => prev.filter((item) => item.id !== id));
    if (editingId === id) annulerEdition();
  }

  function soumettreFormulaire() {
    if (editingId) enregistrerModification();
    else ajouterConstructeur();
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return constructors.filter((item) => {
      if (filterSpecialite !== "toutes" && item.specialite !== filterSpecialite) return false;
      if (filterStatut !== "tous" && item.statut !== filterStatut) return false;
      if (!q) return true;
      const champs = [item.entreprise, item.responsable, item.telephone, item.email, item.zone, item.commentaire, item.specialite]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return champs.includes(q);
    });
  }, [constructors, filterSpecialite, filterStatut, search]);

  const kpiTotal = constructors.length;
  const kpiActifs = useMemo(() => constructors.filter((c) => c.statut === "Actif").length, [constructors]);
  const kpiSpecialites = useMemo(() => {
    const s = new Set(constructors.map((c) => c.specialite).filter(Boolean));
    return s.size;
  }, [constructors]);

  const inputBase = {
    width: "100%",
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: "11px 13px",
    background: BG_CARD,
    fontFamily: FF_DM,
    fontSize: 14,
    color: TAUPE,
    outline: "none",
    boxSizing: "border-box",
  };

  function badgeStatutStyle(statut) {
    if (statut === "Actif") {
      return { background: "rgba(92,138,92,0.14)", color: VERT, border: "1px solid rgba(92,138,92,0.4)" };
    }
    if (statut === "En test") {
      return { background: "rgba(192,154,91,0.16)", color: "#7A5F2A", border: "1px solid rgba(192,154,91,0.4)" };
    }
    if (statut === "Bloqué") {
      return { background: "rgba(184,92,58,0.12)", color: "#A34A2E", border: "1px solid rgba(184,92,58,0.35)" };
    }
    return { background: "rgba(100,100,100,0.1)", color: "#666", border: "1px solid rgba(100,100,100,0.25)" };
  }

  const isTrulyEmpty = constructors.length === 0;
  const noResults = !isTrulyEmpty && filtered.length === 0;

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        `}
      </style><main
        style={{
          minHeight: "100%",
          background: BG,
          fontFamily: FF_DM,
          color: TAUPE,
          padding: "28px 24px 48px",
          boxSizing: "border-box",
        }}
      ><div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* HEADER */}
          <header style={{ marginBottom: 28 }}><div
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                marginBottom: 8,
              }}
            >
              {companyName}
            </div><h1
              style={{
                margin: 0,
                fontFamily: FF_PLAYFAIR,
                fontSize: 32,
                fontWeight: 700,
                color: TAUPE,
                lineHeight: 1.15,
              }}
            >
              Constructeurs
            </h1><p style={{ margin: "10px 0 0", fontSize: 15, color: MUTED, maxWidth: 480 }}>Référencement et suivi des entreprises</p></header>

          {/* KPIs */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 14,
              marginBottom: 28,
            }}
          ><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid ${BORDER}`,
                boxShadow: "0 4px 20px rgba(61,46,32,0.06)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>Total constructeurs</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: TAUPE }}>{kpiTotal}</div></div><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid rgba(92,138,92,0.35)`,
                boxShadow: "0 4px 20px rgba(92,138,92,0.1)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>Actifs</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: VERT }}>{kpiActifs}</div></div><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid rgba(192,154,91,0.45)`,
                boxShadow: "0 4px 20px rgba(192,154,91,0.12)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>Spécialités représentées</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: GOLD }}>{kpiSpecialites}</div></div></div>

          {/* FORMULAIRE */}
          <section
            style={{
              background: BG_CARD,
              borderRadius: 16,
              padding: "22px 24px 20px",
              border: `1px solid ${BORDER}`,
              marginBottom: 28,
              boxShadow: "0 6px 28px rgba(61,46,32,0.07)",
            }}
          ><div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12, marginBottom: 18 }}><h2 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: TAUPE }}>
                {editingId ? "Modifier le constructeur" : "Nouveau constructeur"}
              </h2>
              {editingId && (
                <button
                  type="button"
                  onClick={annulerEdition}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: MUTED,
                    fontFamily: FF_DM,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Annuler
                </button>
              )}
            </div><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginBottom: 16 }}><div><label style={labelStyle}>Nom entreprise</label><input
                  placeholder="Nom de l’entreprise"
                  value={form.entreprise}
                  onChange={(e) => updateField("entreprise", e.target.value)}
                  style={inputBase}
                /></div><div><label style={labelStyle}>Responsable</label><input placeholder="Responsable" value={form.responsable} onChange={(e) => updateField("responsable", e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Téléphone</label><input placeholder="Téléphone" value={form.telephone} onChange={(e) => updateField("telephone", e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Email</label><input type="email" placeholder="Email" value={form.email} onChange={(e) => updateField("email", e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Spécialité</label><select value={form.specialite} onChange={(e) => updateField("specialite", e.target.value)} style={{ ...inputBase, cursor: "pointer" }}>
                  {SPECIALITES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select></div><div><label style={labelStyle}>Zone intervention</label><input placeholder="Zone d’intervention" value={form.zone} onChange={(e) => updateField("zone", e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Statut</label><select value={form.statut} onChange={(e) => updateField("statut", e.target.value)} style={{ ...inputBase, cursor: "pointer" }}>
                  {STATUTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select></div><div><label style={labelStyle}>Commentaire</label><textarea
                  placeholder="Commentaire / note interne"
                  rows={3}
                  value={form.commentaire}
                  onChange={(e) => updateField("commentaire", e.target.value)}
                  style={{ ...inputBase, resize: "vertical", minHeight: 72, lineHeight: 1.45 }}
                /></div></div><div style={{ display: "flex", justifyContent: "flex-end" }}><button
                type="button"
                onClick={soumettreFormulaire}
                style={{
                  padding: "12px 22px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  fontFamily: FF_DM,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(192,154,91,0.45)",
                }}
              >
                {editingId ? "Enregistrer" : "Ajouter"}
              </button></div></section>

          {/* FILTRES */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 20,
              alignItems: "end",
            }}
          ><div><label style={labelStyle}>Spécialité</label><select value={filterSpecialite} onChange={(e) => setFilterSpecialite(e.target.value)} style={{ ...inputBase, cursor: "pointer" }}><option value="toutes">Toutes</option>
                {SPECIALITES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select></div><div><label style={labelStyle}>Statut</label><select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)} style={{ ...inputBase, cursor: "pointer" }}><option value="tous">Tous</option>
                {STATUTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select></div><div style={{ gridColumn: "span 1" }}><label style={labelStyle}>Recherche</label><input placeholder="Nom, contact, zone…" value={search} onChange={(e) => setSearch(e.target.value)} style={inputBase} /></div></div>

          {/* LISTE */}
          <section><h2 style={{ margin: "0 0 18px", fontFamily: FF_PLAYFAIR, fontSize: 24, fontWeight: 700, color: TAUPE }}>Liste des constructeurs</h2>

            {isTrulyEmpty && (
              <div
                style={{
                  textAlign: "center",
                  padding: "56px 24px",
                  background: BG_CARD,
                  borderRadius: 16,
                  border: `1px dashed ${BORDER}`,
                }}
              ><p style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20, color: TAUPE }}>Aucun constructeur enregistré</p><p style={{ margin: 0, fontSize: 14, color: MUTED, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
                  Ajoutez votre première entreprise partenaire pour centraliser le référencement et le suivi.
                </p></div>
            )}

            {noResults && (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 24px",
                  background: BG_CARD,
                  borderRadius: 16,
                  border: `1px solid ${BORDER}`,
                  color: MUTED,
                  fontSize: 15,
                }}
              >
                Aucun résultat pour ces filtres.
              </div>
            )}

            {!isTrulyEmpty && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
                {filtered.map((item) => (
                  <article
                    key={item.id}
                    style={{
                      background: BG_CARD,
                      borderRadius: 16,
                      padding: "18px 20px",
                      border: `1px solid ${BORDER}`,
                      boxShadow: "0 4px 18px rgba(61,46,32,0.06)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  ><div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}><div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          background: `linear-gradient(145deg, ${GOLD} 0%, #A8844A 100%)`,
                          color: "#FFF",
                          fontFamily: FF_PLAYFAIR,
                          fontSize: 20,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 4px 12px rgba(192,154,91,0.35)",
                        }}
                      >
                        {initiale(item.entreprise)}
                      </div><div style={{ flex: 1, minWidth: 0 }}><h3 style={{ margin: "0 0 6px", fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, color: TAUPE, lineHeight: 1.25 }}>
                          {item.entreprise}
                        </h3><div style={{ fontSize: 13, color: TAUPE, lineHeight: 1.5 }}><div><strong style={{ fontWeight: 600 }}>{item.responsable}</strong></div>
                          {item.telephone ? <div>{item.telephone}</div> : null}
                          {item.email ? (
                            <div style={{ wordBreak: "break-word" }}><a href={`mailto:${item.email}`} style={{ color: GOLD, textDecoration: "none" }}>
                                {item.email}
                              </a></div>
                          ) : null}
                        </div></div></div><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}><span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          padding: "5px 10px",
                          borderRadius: 999,
                          background: "rgba(192,154,91,0.16)",
                          color: "#7A5F2A",
                          border: "1px solid rgba(192,154,91,0.35)",
                        }}
                      >
                        {item.specialite}
                      </span>
                      {item.zone ? (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            padding: "5px 10px",
                            borderRadius: 999,
                            background: "rgba(61,46,32,0.08)",
                            color: TAUPE,
                            border: `1px solid rgba(61,46,32,0.15)`,
                          }}
                        >
                          {item.zone}
                        </span>
                      ) : null}
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          padding: "5px 10px",
                          borderRadius: 999,
                          ...badgeStatutStyle(item.statut),
                        }}
                      >
                        {item.statut}
                      </span></div>

                    {item.commentaire ? (
                      <p style={{ margin: 0, fontSize: 13, fontStyle: "italic", color: MUTED, lineHeight: 1.45 }}>{item.commentaire}</p>
                    ) : null}

                    <div style={{ fontSize: 11, color: MUTED }}>Créé le {item.dateCreation}</div><div style={{ display: "flex", gap: 8, marginTop: 4 }}><button
                        type="button"
                        onClick={() => commencerEdition(item)}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          borderRadius: 8,
                          border: `1px solid ${BORDER}`,
                          background: "#FAF8F5",
                          color: TAUPE,
                          fontFamily: FF_DM,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Modifier
                      </button><button
                        type="button"
                        onClick={() => supprimerConstructeur(item.id)}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          borderRadius: 8,
                          border: "1px solid rgba(184,92,58,0.25)",
                          background: "rgba(184,92,58,0.06)",
                          color: "#8B4A32",
                          fontFamily: FF_DM,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Supprimer
                      </button></div></article>
                ))}
              </div>
            )}
          </section></div></main></>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: MUTED,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
